import React, { useState } from 'react'
import { supabase } from '../lib/supabase'

const LoginDebug = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)

  const addResult = (test, success, message, data = null) => {
    setResults(prev => [...prev, { 
      test, 
      success, 
      message, 
      data,
      timestamp: new Date().toLocaleTimeString() 
    }])
  }

  const testConnection = async () => {
    setLoading(true)
    setResults([])
    
    try {
      // Test 1: Check Supabase connection
      addResult('Supabase Client', true, 'Supabase client initialized')
      
      // Test 2: Check if we can reach the database
      try {
        const { data, error } = await supabase.from('users').select('id').limit(1)
        if (error) {
          // Handle specific Supabase errors
          if (error.code === 'PGRST116') {
            throw new Error('Table "users" does not exist. Database may not be properly set up.')
          } else if (error.code === '42501') {
            throw new Error('Permission denied. Check RLS policies for users table.')
          } else if (error.message.includes('JWT')) {
            throw new Error('Authentication token issue. Check Supabase configuration.')
          } else {
            throw error
          }
        }
        addResult('Database Connection', true, 'Successfully connected to database')
      } catch (error) {
        let errorMessage = error.message
        if (error.name === 'AbortError') {
          errorMessage = 'Database connection timeout. Check network connectivity.'
        } else if (error.message.includes('fetch')) {
          errorMessage = 'Network error. Unable to reach Supabase server.'
        }
        addResult('Database Connection', false, `Database error: ${errorMessage}`, error)
      }
      
      // Test 3: Check auth session
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        if (error) throw error
        addResult('Auth Session', !!session, session ? `Active session for: ${session.user.email}` : 'No active session')
      } catch (error) {
        addResult('Auth Session', false, `Session error: ${error.message}`, error)
      }
      
      // Test 4: List existing users (if any)
      try {
        const { data: users, error } = await supabase
          .from('users')
          .select('id, email, name, created_at')
          .limit(10)
        
        if (error) {
          // Handle specific RLS and permission errors
          if (error.code === '42501') {
            throw new Error('Permission denied. User may not be authenticated or RLS policies are blocking access.')
          } else if (error.message.includes('RLS')) {
            throw new Error('Row Level Security policy violation. Check user authentication and policies.')
          } else {
            throw error
          }
        }
        addResult('Users List', true, `Found ${users.length} users in database`, users)
      } catch (error) {
        let errorMessage = error.message
        if (error.name === 'AbortError') {
          errorMessage = 'Query timeout. Database may be overloaded.'
        }
        addResult('Users List', false, `Users query error: ${errorMessage}`, error)
      }
      
    } catch (error) {
      addResult('General Error', false, error.message, error)
    } finally {
      setLoading(false)
    }
  }

  const testLogin = async () => {
    if (!email || !password) {
      addResult('Login Test', false, 'Please enter email and password')
      return
    }
    
    setLoading(true)
    
    try {
      addResult('Login Attempt', true, `Attempting to sign in with: ${email}`)
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) {
        addResult('Login Test', false, `Login failed: ${error.message}`, error)
      } else {
        addResult('Login Test', true, `Login successful for: ${data.user.email}`, data)
        
        // Check if user profile exists
        try {
          const { data: profile, error: profileError } = await supabase
            .from('users')
            .select('*')
            .eq('id', data.user.id)
            .single()
          
          if (profileError) {
            addResult('Profile Check', false, `Profile error: ${profileError.message}`, profileError)
          } else {
            addResult('Profile Check', true, `Profile found: ${profile.name || profile.email}`, profile)
          }
        } catch (profileError) {
          addResult('Profile Check', false, `Profile lookup error: ${profileError.message}`, profileError)
        }
      }
    } catch (error) {
      addResult('Login Test', false, `Login error: ${error.message}`, error)
    } finally {
      setLoading(false)
    }
  }

  const createTestUser = async () => {
    setLoading(true)
    
    const testEmail = `test${Date.now()}@example.com`
    const testPassword = 'testpass123'
    
    try {
      addResult('User Creation', true, `Creating test user: ${testEmail}`)
      
      const { data, error } = await supabase.auth.signUp({
        email: testEmail,
        password: testPassword,
        options: {
          data: {
            name: 'Test User',
            agency: 'Test Agency',
            position: 'Test Position'
          }
        }
      })
      
      if (error) {
        addResult('User Creation', false, `Signup failed: ${error.message}`, error)
      } else {
        addResult('User Creation', true, `User created successfully: ${data.user.email}`, data)
        setEmail(testEmail)
        setPassword(testPassword)
      }
    } catch (error) {
      addResult('User Creation', false, `Signup error: ${error.message}`, error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Login Debug Tool</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Test Controls</h3>
          
          <button
            onClick={testConnection}
            disabled={loading}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Test Connection
          </button>
          
          <button
            onClick={createTestUser}
            disabled={loading}
            className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            Create Test User
          </button>
          
          <div className="space-y-2">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded"
            />
            <button
              onClick={testLogin}
              disabled={loading || !email || !password}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            >
              Test Login
            </button>
          </div>
        </div>
        
        <div>
          <h3 className="text-lg font-semibold mb-4">Test Results</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {results.map((result, index) => (
              <div
                key={index}
                className={`p-3 rounded border-l-4 ${
                  result.success 
                    ? 'bg-green-50 border-green-500 text-green-800'
                    : 'bg-red-50 border-red-500 text-red-800'
                }`}
              >
                <div className="font-medium">{result.test}</div>
                <div className="text-sm">{result.message}</div>
                <div className="text-xs opacity-75">{result.timestamp}</div>
                {result.data && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs">Show Details</summary>
                    <pre className="text-xs mt-1 p-2 bg-gray-100 rounded overflow-x-auto">
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {loading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <div className="mt-2">Running tests...</div>
        </div>
      )}
    </div>
  )
}

export default LoginDebug